import {
  Grid,
  Typography,
  FormControlLabel,
  FormLabel,
  Checkbox,
  FormGroup,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { SyntheticEvent, useCallback, useMemo, useState } from "react";
import BaseButton from "../../shareComponents/button/BaseButton";
import SecondaryButton from "../../shareComponents/button/SecondaryButton";

export interface DiseaseTypeProps {
  nAMD: boolean;
  PCV: boolean;
  DME: boolean;
  RVO: boolean;
  mCNV: boolean;
  others: boolean;
}

export interface DrugTypeProps {
  Faricimab: boolean;
  Ranibizumab: boolean;
  Brolucizumab: boolean;
  Aflibercept: boolean;
}

export type FilterValueProps = {
  diseaseType: DiseaseTypeProps;
  drugType: DrugTypeProps;
};

interface FilterOptionsPopoverProps {
  applyFilter: (apply: boolean, data: FilterValueProps | null) => void;
  value: FilterValueProps;
}

const FilterOptionsPopover = (props: FilterOptionsPopoverProps) => {
  const { applyFilter, value: initValue } = props;
  const { t } = useTranslation();

  const [value, setValue] = useState<FilterValueProps>(initValue);

  const handleToggleAllDiseaseTypeCheckBox = useCallback(
    (event: SyntheticEvent<Element, Event>, checked: boolean) => {
      setValue((val) => ({
        ...val,
        diseaseType: {
          nAMD: checked,
          PCV: checked,
          DME: checked,
          RVO: checked,
          mCNV: checked,
          others: checked,
        },
      }));
    },
    [],
  );

  const handleDiseaseTypeCheckBox = useCallback(
    (
      event: SyntheticEvent<Element, Event>,
      checked: boolean,
      diseaseType: keyof typeof value.diseaseType,
    ) => {
      setValue((val) => ({
        ...val,
        diseaseType: {
          ...val.diseaseType,
          [diseaseType]: checked,
        },
      }));
    },
    [],
  );

  const handleToggleAllDrugTypeCheckBox = useCallback(
    (event: SyntheticEvent<Element, Event>, checked: boolean) => {
      setValue((val) => ({
        ...val,
        drugType: {
          Faricimab: checked,
          Ranibizumab: checked,
          Brolucizumab: checked,
          Aflibercept: checked,
        },
      }));
    },
    [],
  );

  const handleDrugTypeCheckBox = useCallback(
    (
      event: SyntheticEvent<Element, Event>,
      checked: boolean,
      drugType: keyof typeof value.drugType,
    ) => {
      setValue((val) => ({
        ...val,
        drugType: {
          ...val.drugType,
          [drugType]: checked,
        },
      }));
    },
    [],
  );

  const allDiseaseTypeChecked = useMemo(() => {
    const diseaseTypeArray = Object.values(value.diseaseType);
    return !diseaseTypeArray.some((e) => e === false);
  }, [value.diseaseType]);

  const allDrugTypeChecked = useMemo(() => {
    const drugTypeArray = Object.values(value.drugType);
    return !drugTypeArray.some((e) => e === false);
  }, [value.drugType]);

  return (
    <Grid container sx={{ minWidth: 300, maxWidth: 500, p: 2 }}>
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ p: 2 }}>
          {t("dashboardPage.filters")}
        </Typography>
      </Grid>

      <FormGroup>
        <Grid container item xs={12}>
          <Grid item xs={12}>
            <FormLabel id="radio-buttons-group-label">
              {t("singlePharse.diseaseType")}
            </FormLabel>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox />}
              label={t("dashboardPage.allDiseaseTypes")}
              checked={allDiseaseTypeChecked}
              onChange={handleToggleAllDiseaseTypeCheckBox}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <FormControlLabel
              control={<Checkbox />}
              label="nAMD"
              checked={value.diseaseType.nAMD}
              onChange={(event, checked) =>
                handleDiseaseTypeCheckBox(event, checked, "nAMD")
              }
            />
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <FormControlLabel
              control={<Checkbox />}
              label="PCV"
              checked={value.diseaseType.PCV}
              onChange={(event, checked) =>
                handleDiseaseTypeCheckBox(event, checked, "PCV")
              }
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <FormControlLabel
              control={<Checkbox />}
              label="DME"
              checked={value.diseaseType.DME}
              onChange={(event, checked) =>
                handleDiseaseTypeCheckBox(event, checked, "DME")
              }
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <FormControlLabel
              control={<Checkbox />}
              label="RVO"
              checked={value.diseaseType.RVO}
              onChange={(event, checked) =>
                handleDiseaseTypeCheckBox(event, checked, "RVO")
              }
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <FormControlLabel
              control={<Checkbox />}
              label="mCNV"
              checked={value.diseaseType.mCNV}
              onChange={(event, checked) =>
                handleDiseaseTypeCheckBox(event, checked, "mCNV")
              }
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <FormControlLabel
              control={<Checkbox />}
              label="Others"
              checked={value.diseaseType.others}
              onChange={(event, checked) =>
                handleDiseaseTypeCheckBox(event, checked, "others")
              }
            />
          </Grid>
        </Grid>
      </FormGroup>

      <FormGroup>
        <Grid container item xs={12} sx={{ marginTop: 1 }}>
          <Grid item xs={12}>
            <FormLabel id="radio-buttons-group-label">
              {t("singlePharse.drugType")}
            </FormLabel>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox />}
              label={t("dashboardPage.allDrugTypes")}
              checked={allDrugTypeChecked}
              onChange={handleToggleAllDrugTypeCheckBox}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          <Grid item xs={6} sm={6} md={4}>
            <FormControlLabel
              control={<Checkbox />}
              label="Faricimab"
              checked={value.drugType.Faricimab}
              onChange={(event, checked) =>
                handleDrugTypeCheckBox(event, checked, "Faricimab")
              }
            />
          </Grid>

          <Grid item xs={6} sm={6} md={4}>
            <FormControlLabel
              control={<Checkbox />}
              label="Ranibizumab"
              checked={value.drugType.Ranibizumab}
              onChange={(event, checked) =>
                handleDrugTypeCheckBox(event, checked, "Ranibizumab")
              }
            />
          </Grid>
          <Grid item xs={6} sm={6} md={4}>
            <FormControlLabel
              control={<Checkbox />}
              label="Brolucizumab"
              checked={value.drugType.Brolucizumab}
              onChange={(event, checked) =>
                handleDrugTypeCheckBox(event, checked, "Brolucizumab")
              }
            />
          </Grid>
          <Grid item xs={6} sm={6} md={4}>
            <FormControlLabel
              control={<Checkbox />}
              label="Aflibercept"
              checked={value.drugType.Aflibercept}
              onChange={(event, checked) =>
                handleDrugTypeCheckBox(event, checked, "Aflibercept")
              }
            />
          </Grid>
        </Grid>
      </FormGroup>

      <Grid container item xs={12} direction="row-reverse" sx={{ mt: 1 }}>
        <BaseButton sx={{ ml: 1 }} onClick={() => applyFilter(true, value)}>
          {t("singlePharse.apply")}
        </BaseButton>
        <SecondaryButton onClick={() => applyFilter(false, null)}>
          {t("singlePharse.cancel")}
        </SecondaryButton>
      </Grid>
    </Grid>
  );
};

export default FilterOptionsPopover;
